import { useEffect, useState } from "react";
import type { AdminOpenClaw } from "../../../../types/openclaw";
import type { UseAdminClawsResult } from "../../../../hooks/useAdminClaws";
import styles from "./AdminSettingsPanel.module.css";

export type Outcome = { ok: true } | { ok: false; message: string };

// One async admin mutation: busy while in flight, the failure message after.
export const useMutation = (): { busy: boolean; error: string | null; run: (action: () => Promise<Outcome>) => Promise<void> } => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: () => Promise<Outcome>): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await action();
    setBusy(false);
    if (!result.ok) setError(result.message);
  };
  return { busy, error, run };
};

type SwitchRowProps = {
  readonly label: string;
  readonly hint: string;
  readonly on: boolean;
  readonly disabled: boolean;
  readonly error: string | null;
  readonly onToggle: () => void;
};

export const SwitchRow = ({ label, hint, on, disabled, error, onToggle }: SwitchRowProps): JSX.Element => (
  <li className={styles.row}>
    <button type="button" role="switch" aria-checked={on} className={styles.switchRow} data-on={on} onClick={onToggle} disabled={disabled}>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        <span className={styles.hint}>{hint}</span>
      </span>
      <span className={styles.track} aria-hidden><span className={styles.knob} /></span>
    </button>
    {error !== null && <span className={styles.error} role="status">{error}</span>}
  </li>
);

// Raising needs an internal owner (the server enforces it); lowering is always allowed.
const agentAccessHint = (claw: AdminOpenClaw, platformAdmin: boolean): string => {
  if (platformAdmin && claw.agentAccessActive !== true) return "Inactive: the owner or a collaborator is not internal.";
  if (claw.internal !== true) return "Only for instances owned by an internal user.";
  return "Reads every instance's support messages. Only for a dedicated agent with no outbound tools.";
};

type BonusRowProps = { readonly saved: number; readonly onSave: (slots: number) => Promise<Outcome> };

const BonusSlotsRow = ({ saved, onSave }: BonusRowProps): JSX.Element => {
  const [draft, setDraft] = useState(String(saved));
  const { busy, error, run } = useMutation();
  // Re-sync when the saved value changes, so the input never shows a stale number.
  useEffect(() => setDraft(String(saved)), [saved]);
  const parsed = Number(draft);
  const valid = draft.trim() !== "" && Number.isInteger(parsed) && parsed >= 0 && parsed <= 100;
  return (
    <li className={styles.row}>
      <div className={styles.fieldRow}>
        <label className={styles.text} htmlFor="bonus-slots">
          <span className={styles.label}>Bonus integration slots</span>
          <span className={styles.hint}>Added on top of the plan's slots and any purchased ones.</span>
        </label>
        <span className={styles.inputGroup}>
          <input id="bonus-slots" type="number" min={0} max={100} step={1} className={styles.input} value={draft}
            onChange={(e) => setDraft(e.target.value)} disabled={busy} />
          <button type="button" className={styles.save} onClick={() => void run(() => onSave(parsed))} disabled={busy || !valid || parsed === saved}>
            {busy ? "Saving..." : "Save"}
          </button>
        </span>
      </div>
      {error !== null && <span className={styles.error} role="status">{error}</span>}
    </li>
  );
};

type AdminSettingsPanelProps = {
  readonly claw: AdminOpenClaw;
  readonly actions: Pick<UseAdminClawsResult, "setUnlimitedEntitlements" | "setAgentAccess" | "setBonusSlots">;
};

export const AdminSettingsPanel = ({ claw, actions }: AdminSettingsPanelProps): JSX.Element => {
  const unlimited = useMutation();
  const agent = useMutation();
  const platformAdmin = claw.agentAccess === "platform_admin";
  return (
    <section className={styles.card}>
      <h3 className={styles.title}>Admin controls</h3>
      <ul className={styles.list}>
        <SwitchRow label="Unlimited entitlements" hint="Exempt this instance from integration slot and coaching limits."
          on={claw.unlimitedEntitlements} disabled={unlimited.busy} error={unlimited.error}
          onToggle={() => void unlimited.run(() => actions.setUnlimitedEntitlements(claw.tenantId, !claw.unlimitedEntitlements))} />
        <BonusSlotsRow saved={claw.bonusSlots ?? 0} onSave={(slots) => actions.setBonusSlots(claw.tenantId, slots)} />
        <SwitchRow label="Platform admin agent" hint={agentAccessHint(claw, platformAdmin)}
          on={platformAdmin} disabled={agent.busy || (!platformAdmin && claw.internal !== true)} error={agent.error}
          onToggle={() => void agent.run(() => actions.setAgentAccess(claw.tenantId, platformAdmin ? "standard" : "platform_admin"))} />
      </ul>
    </section>
  );
};
